import { bidKeys } from '@tempmee/awards';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAppSelector } from '~/hooks';
import * as Service from '../services/GetBidHistory';

/**
 * Shared React Query hook backing both the BID page and the visibility gate.
 * Query key is office-scoped: switching DSO child re-evaluates the gate +
 * history (GOTU-4820 parent/child rule).
 *
 * @param page - 1-based page number
 * @param enabled - allows callers (e.g. the visibility gate) to skip the fetch
 */
export const useBidHistoryQuery = (page: number, enabled = true) => {
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);

  return useQuery({
    queryKey: [...bidKeys.history(page), selectedOfficeId],
    queryFn: () => Service.getBidHistory({ page }),
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
    placeholderData: keepPreviousData,
  });
};

/**
 * Fetches a page of the member-facing Best-in-Dental history.
 * pageSize is server-fixed — nothing to pass beyond the page number.
 *
 * @param page - 1-based page number
 * @returns items, totalCount, totalPages, isLoading, isError, refetch
 */
export const useGetBidHistory = (page: number) => {
  const query = useBidHistoryQuery(page);

  return {
    items: query.data?.items ?? [],
    toolkitEligible: query.data?.toolkitEligible ?? false,
    totalCount: query.data?.totalCount ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
};
